import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Tooltip from '../Tooltip';
import { useSpaceToolsStore } from './stores/spaceToolsStore';

// Tools Icon Component
const ToolsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-6 h-6"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

// Space Selection Icon
const SpaceSelectionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="w-5 h-5"
  >
    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

// Text Manipulation Icon
const TextManipulationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
  >
    <polyline points="4 7 4 4 20 4 20 7" />
    <line x1="9" y1="20" x2="15" y2="20" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

interface SpaceToolsButtonProps {
  position?: 'bottom-right' | 'bottom-left';
}

const SpaceToolsButton: React.FC<SpaceToolsButtonProps> = ({ position = 'bottom-right' }) => {
  const { menuOpen, toggleMenu, setActivePanel, setMenuOpen } = useSpaceToolsStore();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, setMenuOpen]);

  const menuPosition = position === 'bottom-right'
    ? 'right-0 bottom-full mb-2'
    : 'left-0 bottom-full mb-2';

  const handleSpaceSelectionClick = () => {
    setActivePanel('spaceSelection');
  };

  const handleTextManipulationClick = () => {
    setActivePanel('textManipulation');
  };

  return (
    <div className="relative z-50">
      {/* Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${menuPosition} w-64`}
          >
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
              {/* Space Selection Option */}
              <button
                onClick={handleSpaceSelectionClick}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                  <SpaceSelectionIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Space Selection</p>
                  <p className="text-xs text-gray-500">Select and analyze spaces</p>
                </div>
              </button>

              {/* Divider */}
              <div className="h-px bg-gray-200" />

              {/* Text Manipulation Option */}
              <button
                onClick={handleTextManipulationClick}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-btn-fab text-btn-fab-text flex items-center justify-center group-hover:scale-110 transition-transform">
                  <TextManipulationIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Text Manipulation</p>
                  <p className="text-xs text-gray-500">Edit text properties</p>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <Tooltip text="Space & Text Tools" className={position === 'bottom-right' ? '!left-1/2 !-translate-x-full' : ''}>
        <button
          ref={buttonRef}
          onClick={toggleMenu}
          className={`p-3 bg-btn-fab text-btn-fab-text rounded-full shadow-lg hover:scale-110 focus:outline-none focus:ring-2 focus:ring-btn-fab-ring focus:ring-offset-2 transition-transform duration-150 ease-in-out ${menuOpen ? 'scale-110' : ''
            }`}
          aria-label="Open Space Tools Menu"
          title="Space & Text Tools"
        >
          <ToolsIcon />
        </button>
      </Tooltip>
    </div>
  );
};

export default SpaceToolsButton;
